import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReportTableResponse } from '../../api/types';
import { formatCell, isNumeric, type ColumnType } from './reportFormat';

/**
 * The comparison a report is actually asking about.
 *
 * Until this existed the reports hub answered every question with a table, which is the right way
 * to *hold* a report and the wrong way to read one. "Which branch earns most" and "which doctor is
 * busiest" are rankings, and a ranking read down a column of grouped decimals is a ranking the
 * reader has to compute; the same eight rows as bars are answered before they are read. The table
 * stays underneath, because the exact figures are what somebody exports and quotes.
 *
 * Three decisions worth stating, because each one had an obvious alternative that is worse here.
 *
 * **Horizontal bars, not vertical.** The dimension is branch, doctor, service or package, and
 * every one of those is a name of unpredictable length — in two scripts. Vertical bars would need
 * the labels rotated or truncated; horizontal ones give a name as much room as it needs and put
 * the ranking in reading order.
 *
 * **HTML and CSS, not SVG.** The whole product mirrors for Arabic from one stylesheet (NFR-006),
 * and SVG has no logical axis: an `x` of 0 is the left edge in both languages, so every bar,
 * label and tick would need mirroring by hand. Built from a grid and a `<div>` per bar, the chart
 * flips with everything else and costs nothing.
 *
 * **One series.** The palette work in tokens.css found that this family supports exactly two
 * categorical hues before they stop separating under colour-vision deficiency, and a report
 * comparing gross against net against VAT would want six. So the reader picks *which* measure to
 * plot and the rest stay in the table — which is also the honest answer, since those columns are
 * not comparable to each other anyway.
 */

/** Anything a bar can be drawn from. Percentages and hours are ranked as readily as money. */
const PLOTTABLE: readonly ColumnType[] = ['MONEY', 'INTEGER', 'PERCENT', 'HOURS', 'DAYS'];

/** A ranking longer than this stops being a comparison and becomes a list; the table has the rest. */
const MAX_BARS = 12;

const NO_COLUMNS: NonNullable<ReportTableResponse['columns']> = [];
const NO_ROWS: NonNullable<ReportTableResponse['rows']> = [];

export function ReportChart({
  table,
  heading,
}: {
  readonly table: ReportTableResponse;
  readonly heading: (columnKey: string) => string;
}) {
  const { t } = useTranslation();
  /* `?? []` produces a new array on every render, so a memo that depends on one recomputes every
     time and the memo is doing nothing. Both defaults are hoisted to module scope, where the empty
     array is the same object each time. */
  const columns = table.columns ?? NO_COLUMNS;
  const rows = table.rows ?? NO_ROWS;

  const labelKey = columns.find((column) => !isNumeric(column.type))?.key ?? 'label';
  const measures = useMemo(
    () =>
      columns.filter(
        (column) => column.key && column.type && PLOTTABLE.includes(column.type as ColumnType),
      ),
    [columns],
  );

  const [chosen, setChosen] = useState<string | null>(null);
  /* Money first, because seven of the eight reports are asked about money, and the first numeric
     column otherwise. The reader's choice wins once they have made one — but it is reset by the
     key below when the report changes, since a column named `net` in one report may not exist in
     the next. */
  const measure =
    measures.find((column) => column.key === chosen) ??
    measures.find((column) => column.type === 'MONEY') ??
    measures[0];

  const plotted = useMemo(() => {
    if (!measure?.key) {
      return [];
    }
    return rows
      .map((row) => ({
        label: String(row[labelKey] ?? '—'),
        value: Number(row[measure.key ?? ''] ?? 0),
        raw: row[measure.key ?? ''],
      }))
      .filter((point) => Number.isFinite(point.value))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_BARS);
  }, [rows, labelKey, measure]);

  /* Every bar is a share of the largest, not of the total: the question is "how do these compare",
     and scaling to a sum makes the leader short whenever there are many rows. A zero maximum would
     divide by zero, so a report of all-zeroes draws a row of empty tracks rather than NaN. */
  const largest = plotted.reduce((running, point) => Math.max(running, point.value), 0);

  if (!measure || plotted.length < 2) {
    // One row is not a comparison, and no numeric column is not a chart. The table says it all.
    return null;
  }

  return (
    <div className="chart">
      {measures.length > 1 ? (
        <div className="tabs" role="tablist" aria-label={t('reports.measure')}>
          {measures.map((column) => (
            <button
              key={column.key}
              type="button"
              role="tab"
              aria-selected={column.key === measure.key}
              className={column.key === measure.key ? 'tab tab--active' : 'tab'}
              onClick={() => setChosen(column.key ?? null)}
            >
              {heading(column.key ?? '')}
            </button>
          ))}
        </div>
      ) : null}

      <ol className="bars">
        {plotted.map((point) => (
          <li className="bars__row" key={point.label}>
            <span className="bars__label" title={point.label}>
              {point.label}
            </span>
            <span className="bars__track">
              <span
                className="bars__bar"
                style={{ inlineSize: `${largest > 0 ? (point.value / largest) * 100 : 0}%` }}
              />
            </span>
            <span className="bars__value numeric">
              {formatCell(point.raw, measure.type, table.currency)}
            </span>
          </li>
        ))}
      </ol>

      {/* Never a silent cap: a chart that quietly drew the first twelve of forty branches would
          read as the whole clinic group. */}
      {rows.length > plotted.length ? (
        <p className="muted">{t('reports.chartTruncated', { shown: plotted.length, total: rows.length })}</p>
      ) : null}
    </div>
  );
}
